/**
 * Theme Helper Functions
 * 
 * These helpers make it easy to update existing components to support all themes
 * without requiring a full rewrite. They provide backward-compatible ways to
 * handle theme conditionals.
 */

import type { ThemeName } from './theme-config';

/**
 * Get theme-aware class based on simple dark/light mapping
 * 
 * This helper automatically maps new themes to the closest match:
 * - 'dark' and 'dark-slate' → darkClass
 * - 'light' and 'tenant-selector' → lightClass
 * 
 * Use this to quickly update existing conditionals:
 * 
 * BEFORE:
 * className={theme === 'dark' ? 'bg-slate-800' : 'bg-slate-100'}
 * 
 * AFTER:
 * className={getThemeClass(theme, 'bg-slate-800', 'bg-slate-100')}
 */
export function getThemeClass(
  theme: ThemeName,
  darkClass: string,
  lightClass: string
): string {
  // Map themes to dark or light
  if (theme === 'dark' || theme === 'dark-slate') {
    return darkClass;
  }
  // tenant-selector uses light theme styling as base
  if (theme === 'tenant-selector' || theme === 'light') {
    return lightClass;
  }
  // Default to light
  return lightClass;
}

/**
 * Get theme-aware class with explicit mappings for all themes
 * 
 * Use this when you need specific styling for tenant-selector or dark-slate:
 * 
 * className={getThemeClassExplicit(
 *   theme,
 *   'bg-slate-800',        // dark
 *   'bg-slate-100',        // light
 *   'bg-white',            // tenant-selector
 *   'bg-slate-800'         // dark-slate
 * )}
 */
export function getThemeClassExplicit(
  theme: ThemeName,
  darkClass: string,
  lightClass: string,
  tenantSelectorClass?: string,
  darkSlateClass?: string
): string {
  switch (theme) {
    case 'dark':
      return darkClass;
    case 'light':
      return lightClass;
    case 'tenant-selector':
      return tenantSelectorClass || lightClass;
    case 'dark-slate':
      return darkSlateClass || darkClass;
    default:
      return lightClass;
  }
}

/**
 * Check if theme is a dark variant
 */
export function isDarkTheme(theme: ThemeName): boolean {
  return theme === 'dark' || theme === 'dark-slate';
}

/**
 * Check if theme is a light variant
 */
export function isLightTheme(theme: ThemeName): boolean {
  return theme === 'light' || theme === 'tenant-selector';
}

/**
 * Get theme variant (dark or light)
 * Useful for components that only need dark/light distinction
 */
export function getThemeVariant(theme: ThemeName): 'dark' | 'light' {
  return isDarkTheme(theme) ? 'dark' : 'light';
}

/**
 * Smart theme class mapper
 * 
 * Automatically determines the best class based on theme.
 * For tenant-selector, you can provide specific overrides.
 * 
 * Example:
 * className={smartThemeClass(theme, {
 *   dark: 'bg-slate-800 text-white',
 *   light: 'bg-slate-100 text-slate-900',
 *   'tenant-selector': 'bg-white text-gray-900', // optional override
 * })}
 */
export function smartThemeClass(
  theme: ThemeName,
  classes: {
    dark: string;
    light: string;
    'tenant-selector'?: string;
    'dark-slate'?: string;
  }
): string {
  switch (theme) {
    case 'dark':
      return classes.dark;
    case 'light':
      return classes.light;
    case 'tenant-selector':
      return classes['tenant-selector'] || classes.light;
    case 'dark-slate':
      return classes['dark-slate'] || classes.dark;
    default:
      return classes.light;
  }
}
